import React from 'react';
import { View, Pressable, ViewStyle, StyleProp, LayoutChangeEvent } from 'react-native';
import type { AccessibilityRole, AccessibilityState } from 'react-native';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { useHoverStyle } from '@/hooks/shared/useHoverStyle';
import { WEB_STYLES } from '@/utils/webStyles';

interface GlassCardProps {
  borderRadius?: number;
  dashed?: boolean;
  padding?: number;
  style?: StyleProp<ViewStyle>;
  interactive?: boolean;
  onPress?: () => void;
  onLayout?: (e: LayoutChangeEvent) => void;
  variant?: 'soft' | 'strong';
  /** Forwarded to the card's host: the Pressable when interactive, the View otherwise. */
  accessibilityRole?: AccessibilityRole;
  accessibilityLabel?: string;
  accessibilityState?: AccessibilityState;
  testID?: string;
  children: React.ReactNode;
}

const InteractiveGlassCard: React.FC<GlassCardProps> = ({
  borderRadius = REDESIGN_SPACING.cardBorderRadius,
  dashed = false,
  padding = REDESIGN_SPACING.cardPadding,
  style,
  onPress,
  onLayout,
  variant = 'soft',
  accessibilityRole,
  accessibilityLabel,
  accessibilityState,
  testID,
  children,
}) => {
  const { colors } = useThemeColors();
  // The `card` kind: border → card.borderHover, card.shadowHover, 1px lift
  // (no lift under reduced motion), cursor and transition at rest.
  const { hoverProps, viewStyle } = useHoverStyle('card');
  const backgroundColor = variant === 'strong' ? colors.card.bgStrong : colors.card.bg;

  return (
    <Pressable
      onPress={onPress}
      onLayout={onLayout}
      accessibilityRole={accessibilityRole}
      accessibilityLabel={accessibilityLabel}
      accessibilityState={accessibilityState}
      testID={testID}
      style={({ pressed }) => [
        { backgroundColor, borderWidth: 1, overflow: 'hidden' as const },
        webBlurStyle,
        {
          borderRadius,
          padding,
          borderStyle: dashed ? 'dashed' : 'solid',
          borderColor: dashed ? colors.card.borderDashed : colors.card.border,
        },
        viewStyle,
        pressed ? { opacity: 0.7 } : null,
        style,
      ]}
      {...hoverProps}
    >
      {children}
    </Pressable>
  );
};

const StaticGlassCard: React.FC<Omit<GlassCardProps, 'interactive' | 'onPress'>> = ({
  borderRadius = REDESIGN_SPACING.cardBorderRadius,
  dashed = false,
  padding = REDESIGN_SPACING.cardPadding,
  style,
  onLayout,
  variant = 'soft',
  accessibilityRole,
  accessibilityLabel,
  accessibilityState,
  testID,
  children,
}) => {
  const { colors } = useThemeColors();
  const backgroundColor = variant === 'strong' ? colors.card.bgStrong : colors.card.bg;

  return (
    <View
      onLayout={onLayout}
      accessibilityRole={accessibilityRole}
      accessibilityLabel={accessibilityLabel}
      accessibilityState={accessibilityState}
      testID={testID}
      style={[
        { backgroundColor, borderWidth: 1, overflow: 'hidden' as const },
        webCardStyle,
        {
          borderRadius,
          padding,
          borderStyle: dashed ? 'dashed' : 'solid',
          borderColor: dashed ? colors.card.borderDashed : colors.card.border,
        },
        style,
      ]}
    >
      {children}
    </View>
  );
};

export const GlassCard: React.FC<GlassCardProps> = React.memo(({
  interactive = false,
  ...props
}) => {
  if (interactive) {
    return <InteractiveGlassCard {...props} />;
  }
  return <StaticGlassCard {...props} />;
});

GlassCard.displayName = 'GlassCard';

// Static cards keep their border-color transition (unused now that nothing
// animates it, harmless); interactive cards take their transition from the
// hook, so they only need the blur.
const webBlurStyle = { ...WEB_STYLES.backdropBlur(16) } as any;
const webCardStyle = {
  ...WEB_STYLES.backdropBlur(16),
  ...WEB_STYLES.transition('border-color', '0.15s', 'ease'),
} as any;
